/* === AgTooltip -- Anagogia Design System === */

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

import { usePortalContainer } from '../utils/usePortalContainer';

import styles from './AgTooltip.module.css';

interface AgTooltipProps {
  content: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  children: React.ReactNode;
  className?: string;
}

interface Position {
  top: number;
  left: number;
}

const OFFSET = 8;

function computePosition(
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  placement: NonNullable<AgTooltipProps['placement']>,
): Position {
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = anchorRect.top - tooltipRect.height - OFFSET;
      left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'bottom':
      top = anchorRect.bottom + OFFSET;
      left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'left':
      top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;
      left = anchorRect.left - tooltipRect.width - OFFSET;
      break;
    case 'right':
      top = anchorRect.top + anchorRect.height / 2 - tooltipRect.height / 2;
      left = anchorRect.right + OFFSET;
      break;
  }

  // Clamp to viewport
  const padding = 4;
  left = Math.max(
    padding,
    Math.min(left, window.innerWidth - tooltipRect.width - padding),
  );
  top = Math.max(
    padding,
    Math.min(top, window.innerHeight - tooltipRect.height - padding),
  );

  return { top, left };
}

const ARROW_CLASSES: Record<string, string> = {
  top: styles.arrowTop,
  bottom: styles.arrowBottom,
  left: styles.arrowLeft,
  right: styles.arrowRight,
};

/**
 * Infobulle au survol, positionnable.
 * @category Feedback
 */
function AgTooltip({
  content,
  placement = 'top',
  delay = 200,
  children,
  className,
}: AgTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  // En plein écran, un portail dans `document.body` n'est jamais peint :
  // l'infobulle suit l'élément plein écran qui contient son ancre.
  const portalContainer = usePortalContainer(anchorRef, visible);

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  // Au focus, l'infobulle ne s'affiche que pour le clavier (`:focus-visible`) :
  // un clic, ou le focus qu'une `AgModal` rend à son déclencheur en se
  // fermant, ne doit pas la réveiller. Un navigateur qui ne connaît pas le
  // sélecteur lève : on garde alors l'affichage à tout focus.
  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLSpanElement>) => {
      let clavier = true;
      try {
        clavier = e.target.matches(':focus-visible');
      } catch {
        clavier = true;
      }
      if (clavier) show();
    },
    [show],
  );

  // Appuyer sur la cible (pointeur) ou l'activer (clic, Entrée, Espace) masque
  // l'infobulle et annule l'affichage différé : si l'action ouvre une fenêtre,
  // l'infobulle resterait sinon peinte par-dessus tant que le pointeur ne bouge
  // pas (l'ancre, couverte par le fond de la fenêtre, ne reçoit pas de
  // `mouseleave`). Elle revient au prochain survol ou focus clavier. Une cible
  // désactivée ne fait rien : son infobulle (souvent la raison) reste.
  const handlePress = useCallback(
    (e: React.SyntheticEvent<HTMLSpanElement>) => {
      if (e.target instanceof Element && e.target.closest(':disabled')) return;
      hide();
    },
    [hide],
  );

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Reposition when visible
  useEffect(() => {
    if (!visible || !anchorRef.current || !tooltipRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    setPosition(computePosition(anchorRect, tooltipRect, placement));
  }, [visible, placement]);

  const arrowClass = ARROW_CLASSES[placement];

  const tooltip = visible
    ? createPortal(
        <div
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          className={[styles.tooltip, styles[placement], className]
            .filter(Boolean)
            .join(' ')}
          style={{ top: position.top, left: position.left }}
        >
          {content}
          <span
            className={[styles.arrow, arrowClass].filter(Boolean).join(' ')}
            aria-hidden="true"
          />
        </div>,
        portalContainer,
      )
    : null;

  return (
    <>
      <span
        ref={anchorRef}
        className={styles.anchor}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={handleFocus}
        onBlur={hide}
        onPointerDown={handlePress}
        onClick={handlePress}
        aria-describedby={visible ? tooltipId : undefined}
      >
        {children}
      </span>
      {tooltip}
    </>
  );
}

export { AgTooltip };
export type { AgTooltipProps };
export default AgTooltip;
